import React, { useCallback, useEffect, useMemo, useState } from "react";
// (ImportCsvDialog not used on this page anymore)
// P0 TENANT ISOLATION: Data refetches automatically via API interceptor when tenant context changes

import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { queryKeys } from "@/lib/queryClient";
import {
  useLeadsList,
  useLeadFilters,
} from "@/hooks/useLeads";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/contexts/PageTitleContext";
// (ImportCsvWizard now handled within AddLeadDialog)

import AddLeadDialog from "@/components/leads/AddLeadDialog";
import MultiSelectFilter from "@/components/leads/MultiSelectFilter";

import LeadTableColumnsDialog from "@/components/leads/LeadTableColumnsDialog";
import InCardFilterPanel from "@/components/leads/InCardFilterPanel";

import CustomViewsDialog from "@/components/leads/CustomViewsDialog";





import { ArrowDownAZ, ArrowUpAZ, ArrowUpDown, ChevronDown, Download, ListFilter, Plus, RefreshCw, Search, Settings2, Trash2, AlertTriangle, Users2, ShieldAlert, ShieldCheck, ShieldX, Clock, Users } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ConfidenceBadge } from "@/components/attribution/ConfidenceBadge";


const DEFAULT_LIMIT = 10;

const EXPORT_FIELDS = [
  "id",
  "team_id",
  "created_by_user_id",
  "status",
  "lead_source",
  "campaign_name",
  "ad_set_name",
  "ad_name",
  "form_name",
  "lead_external_id",
  "full_name",
  "phone",
  "email",
  "state",
  "county",
  "timezone_label",
  "estimated_income_range",
  "estimated_home_value_range",
  "dob",
  "lead_type",
  "referral_agent_name",
  "lead_create_date",
  "created_at",
  "updated_at",
  "is_quarantined",
  "compliance_status",
  "engagement_sync_status",
  "attribution_confidence",
];

const LEAD_FIELD_OPTIONS = EXPORT_FIELDS.map((key) => ({
  key,
  label: prettifyFieldLabel(key),
}));

const DEFAULT_VISIBLE_FILTERS = [
  "lead_create_date",
  "campaign_name",
  "form_name",
  "status",
  "lead_source",
  "state",
  "county",
  "lead_type",
  "custom_filters",
];

const DEFAULT_TABLE_COLUMNS = [
  "status",
  "lead_source",
  "attribution_confidence",
  "full_name",
  "state",
  "lead_type",
  "lead_create_date",
];



const PILL_CLASSES = [
  "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20",
  "bg-emerald-500/20 text-emerald-100 ring-emerald-400/20",
  "bg-sky-500/20 text-sky-100 ring-sky-400/20",
  "bg-amber-500/20 text-amber-100 ring-amber-400/20",
  "bg-fuchsia-500/20 text-fuchsia-100 ring-fuchsia-400/20",
  "bg-rose-500/20 text-rose-100 ring-rose-400/20",
  "bg-violet-500/20 text-violet-100 ring-violet-400/20",
];

function hashToIndex(s) {
  const str = String(s || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h % PILL_CLASSES.length;
}

function pillClass(value) {
  if (!value || value === "—") return PILL_CLASSES[0];
  return PILL_CLASSES[hashToIndex(value)];
}

function statusLabel(status) {
  const s = String(status || "").trim();
  if (!s) return "New";  // Default to "New" with capital N
  // Capitalize first letter for consistent display
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// Badge color classes based on lead status
function statusBadgeClass(status) {
  const label = statusLabel(status).toLowerCase();
  switch (label) {
    case "sold":
      return "bg-emerald-500/20 text-emerald-300 ring-emerald-400/30";  // Green for Sold
    case "new":
      return "bg-teal-500/20 text-teal-300 ring-teal-400/30";  // Teal for New
    case "converted":
    case "booked":
      return "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20";
    default:
      return "bg-white/10 text-slate-100 ring-white/10";
  }
}

function formatMmddyyyy(value) {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }
  return s;
}

function prettifyFieldLabel(key) {
  if (!key) return "";
  const s = String(key);
  // Known overrides
  if (s === "dob") return "DOB";
  if (s === "lead_external_id") return "GHL Contact ID";
  if (s === "lead_type") return "Vertical";
  if (s === "created_by_user_id") return "Created By User ID";
  if (s === "team_id") return "Account ID";
  // MA-BE-078: Financial enrichment field labels
  if (s === "estimated_income_range") return "Est. Income";
  if (s === "estimated_home_value_range") return "Est. Home Value";
  if (s === "timezone_label") return "Time Zone";
  if (s === "attribution_confidence") return "Confidence";

  return s
    .split("_")
    .filter(Boolean)
    .map((w) => {
      const lw = w.toLowerCase();
      if (lw === "id") return "ID";
      if (lw === "api") return "API";
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join(" ");
}

function formatCellValue(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (key === "lead_create_date" || key === "dob" || key === "created_at" || key === "updated_at") {
    return formatMmddyyyy(value);
  }
  return String(value);
}



function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function toCsvCell(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return '"' + s.replaceAll('"', '""') + '"';
  }
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(toCsvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function AddLeadDialogLegacy() {
  return null;
}

export default function LeadsPage() {
  usePageTitle("Leads");
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for automatic refetch
  const { selectedTenantId } = useTenant();
  
  // P0 TENANT ISOLATION: Use centralized tenant path builder
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  const [items, setItems] = useState([]);

  // Add Leads dropdown + dialogs
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadMode, setAddLeadMode] = useState("single"); // single | csv

  const [total, setTotal] = useState(0);
  // Note: loading state is now derived from React Query (see Combined loading state below)

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  // Zoho-style filter panel
  const [zohoFiltersOpen, setZohoFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("leads_filters_open");
      return stored ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("leads_filters_open", String(zohoFiltersOpen));
    } catch {
      // ignore
    }
  }, [zohoFiltersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  const [customViewsOpen, setCustomViewsOpen] = useState(false);
  const [activeCustomViewId, setActiveCustomViewId] = useState("all");

  useEffect(() => {
    // When switching custom view, keep state consistent + persist active view id
    if (prefsLoading) return;
    saveLeadsPrefs({ ...leadsPrefs, active_custom_view_id: activeCustomViewId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomViewId]);


  const [filterOptions, setFilterOptions] = useState({
    campaign_names: [],
    form_names: [],
    lead_sources: [],
    statuses: [],
    states: [],
    counties: [],
    lead_types: [],
  });

  const [prefsLoading, setPrefsLoading] = useState(true);
  const [leadsPrefs, setLeadsPrefs] = useState({
    visible_filters: DEFAULT_VISIBLE_FILTERS,
    custom_filters: [],
    table_columns: DEFAULT_TABLE_COLUMNS,
    all_table_columns: DEFAULT_TABLE_COLUMNS,
    custom_views: [],
    active_custom_view_id: "all",
  });

  const customFilters = Array.isArray(leadsPrefs.custom_filters) ? leadsPrefs.custom_filters : [];
  const allTableColumns = Array.isArray(leadsPrefs.all_table_columns) && leadsPrefs.all_table_columns.length > 0
    ? leadsPrefs.all_table_columns
    : leadsPrefs.table_columns;
  const tableColumns =
    activeCustomViewId === "all"
      ? allTableColumns
      : Array.isArray(leadsPrefs.table_columns) && leadsPrefs.table_columns.length > 0
        ? leadsPrefs.table_columns
        : DEFAULT_TABLE_COLUMNS;
  const customViews = Array.isArray(leadsPrefs.custom_views) ? leadsPrefs.custom_views : [];

  const [columnsOpen, setColumnsOpen] = useState(false);

  const openColumnsDialog = () => setColumnsOpen(true);

  const leadsCardRef = React.useRef(null);

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
    setOffset(0);
  };

  const clearSort = () => {
    setSortBy(null);
    setSortDir("desc");
    setOffset(0);
  };



  const filterFieldDefs = useMemo(() => {
    const selectKeys = new Set([
      "campaign_name",
      "form_name",
      "lead_source",
      "status",
      "state",
      "county",
      "lead_type",
      "is_quarantined",
      "compliance_status",
      "engagement_sync_status",
    ]);
    return EXPORT_FIELDS.map((key) => {
      let type = "text";
      if (selectKeys.has(key)) type = "select";
      if (key === "lead_create_date" || key === "dob" || key === "created_at" || key === "updated_at") type = "date";
      return { key, label: prettifyFieldLabel(key), type };
    }).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, []);

  const optionsByField = useMemo(
    () => ({
      campaign_name: filterOptions.campaign_names,
      form_name: filterOptions.form_names,
      lead_source: filterOptions.lead_sources,
      status: filterOptions.statuses,
      state: filterOptions.states,
      county: filterOptions.counties,
      lead_type: filterOptions.lead_types,
      is_quarantined: ["true", "false"],
      compliance_status: ["eligible", "missing_trustedform_cert", "manual_trustedform_override"],
      engagement_sync_status: ["pending", "confirmed", "failed"],
    }),
    [filterOptions]
  );

  const filtersActive = useMemo(() => Object.keys(filtersApplied || {}).length > 0, [filtersApplied]);

  const activeViewLabel = useMemo(() => {
    if (activeCustomViewId === "all") return "All Leads";
    const v = (customViews || []).find((x) => x.id === activeCustomViewId);
    return v?.name || "Custom View";
  }, [activeCustomViewId, customViews]);

  const serializeZohoFilters = (obj) => {
    const clauses = [];
    Object.entries(obj || {}).forEach(([field, cfg]) => {
      if (!cfg?.enabled) return;
      const operator = cfg.operator;
      if (!operator) return;
      const dateNoValueOps = new Set([
        "this_week",
        "this_month",
        "this_year",
        "last_week",
        "last_month",
        "last_year",
        "last_30_days",
        "last_60_days",
        "last_90_days",
        "ytd",
      ]);

      if (["is_empty", "is_not_empty"].includes(operator) || dateNoValueOps.has(operator)) {
        clauses.push({ field, operator });
        return;
      }
      if (cfg.values && Array.isArray(cfg.values) && cfg.values.length > 0) {
        clauses.push({ field, operator, values: cfg.values });
        return;
      }
      const v = String(cfg.value || "").trim();
      if (!v) return;

      if (operator === "between") {
        const vTo = String(cfg.value_to || "").trim();
        if (vTo) clauses.push({ field, operator, value: v, value_to: vTo });
        return;
      }

      clauses.push({ field, operator, value: v });
    });
    return clauses;
  };

  const applyZohoDraft = () => {
    setFiltersApplied(filtersDraft);
    setOffset(0);
  };

  const clearZohoFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
    setOffset(0);
  };

  useEffect(() => {
    // keep draft in sync when applied changes externally (custom view)
    setFiltersDraft(filtersApplied);
  }, [filtersApplied]);



  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  useEffect(() => {
    if (typeof leadsPrefs.page_size === "number") {
      setLimit(leadsPrefs.page_size);
    }
  }, [leadsPrefs.page_size]);

  // Sorting (session-only)
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  // Column sizing + horizontal scroll
  const COL_MIN_PX = 160;
  const tableMinWidthPx = useMemo(() => {
    const n = tableColumns.length;
    // checkbox (46) + settings (44) + gaps/padding overhead (~80) + N columns
    return 46 + 44 + 80 + n * COL_MIN_PX;
  }, [tableColumns, COL_MIN_PX]);

  const columnsGridStyle = useMemo(() => {
    const n = tableColumns.length;
    return {
      gridTemplateColumns: `repeat(${n}, minmax(${COL_MIN_PX}px, 1fr))`,
      minWidth: `${n * COL_MIN_PX}px`,
    };
  }, [tableColumns]);

  const [offset, setOffset] = useState(0);

  const [selected, setSelected] = useState(() => new Set());

  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState("selected"); // selected | filtered
  const [exportFields, setExportFields] = useState(() => new Set(EXPORT_FIELDS));
  const [exporting, setExporting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showHouseholdMembers, setShowHouseholdMembers] = useState(false);

  // ===== REACT QUERY HOOKS =====
  
  // Leads list query
  const leadsQuery = useLeadsList({
    filters: filtersApplied,
    search: qDebounced,
    page: Math.floor(offset / limit),
    pageSize: limit,
    sortBy,
    sortDir,
    includeHouseholdMembers: showHouseholdMembers,
    enabled: !prefsLoading,
  });

  // Filter options query
  const filtersQuery = useLeadFilters();

  // Sync React Query data to local state for component compatibility
  useEffect(() => {
    if (leadsQuery.data) {
      setItems(leadsQuery.data.items || []);
      setTotal(leadsQuery.data.total || 0);
      setSelected(new Set());
    }
  }, [leadsQuery.data]);

  // Sync filter options from query
  useEffect(() => {
    if (filtersQuery.data) {
      setFilterOptions(filtersQuery.data);
    }
  }, [filtersQuery.data]);

  // Combined loading state
  const loading = prefsLoading || leadsQuery.isLoading;

  // Function to invalidate leads queries (for refetch after mutations)
  const invalidateLeadsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.leads.all(selectedTenantId),
    });
  }, [queryClient, selectedTenantId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/user-prefs");
        const next = res.data?.leads || {};
        setLeadsPrefs((prev) => {
          const activeId = typeof next.active_custom_view_id === "string" ? next.active_custom_view_id : prev.active_custom_view_id;
          const allColumns = Array.isArray(next.all_table_columns) && next.all_table_columns.length > 0
            ? next.all_table_columns
            : Array.isArray(next.table_columns) && next.table_columns.length > 0
              ? next.table_columns
              : prev.all_table_columns;
          const tableCols = Array.isArray(next.table_columns) && next.table_columns.length > 0 ? next.table_columns : prev.table_columns;
          return {
            ...prev,
            ...next,
            visible_filters: Array.isArray(next.visible_filters) ? next.visible_filters : prev.visible_filters,
            custom_filters: Array.isArray(next.custom_filters) ? next.custom_filters : prev.custom_filters,
            table_columns: activeId === "all" ? allColumns : tableCols,
            all_table_columns: allColumns,
            custom_views: Array.isArray(next.custom_views) ? next.custom_views : prev.custom_views,
            active_custom_view_id: activeId,
          };
        });
        if (typeof next.active_custom_view_id === "string") {
          setActiveCustomViewId(next.active_custom_view_id);
        }
      } catch (e) {
        // ignore
      } finally {
        setPrefsLoading(false);
      }
    })();
  }, []);

  const saveLeadsPrefs = async (next) => {
    setLeadsPrefs(next);
    try {
      const res = await api.put("/user-prefs", { leads: next });
      const saved = res.data?.leads || next;
      setLeadsPrefs((prev) => ({ ...prev, ...saved }));
      toast({ title: "Saved" });
    } catch (e) {
      toast({ title: "Could not save", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  // REACT QUERY MIGRATION: Manual fetch effects removed
  // - fetchLeads replaced by useLeadsList hook
  // - fetchFilters replaced by useLeadFilters hook
  // - location.key and custom event refetch logic no longer needed (React Query handles cache invalidation)

  const allOnPageSelected = useMemo(() => items.length > 0 && items.every((x) => selected.has(x.id)), [items, selected]);
  const anySelected = selected.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        items.forEach((x) => next.delete(x.id));
      } else {
        items.forEach((x) => next.add(x.id));
      }
      return next;
    });
  };

  const selectedRows = useMemo(() => items.filter((x) => selected.has(x.id)), [items, selected]);

  const openExportDialog = () => {
    setExportScope(anySelected ? "selected" : "filtered");
    setExportFields(new Set(EXPORT_FIELDS));
    setExportOpen(true);
  };

  const fetchAllFilteredLeads = async () => {
    const all = [];
    const pageLimit = 500;
    let nextOffset = 0;

    while (true) {
      const res = await api.get("/leads", {
        params: {
          q: qDebounced || undefined,
          filters: JSON.stringify(serializeZohoFilters(filtersApplied)),
          include_household_members: showHouseholdMembers,
          limit: pageLimit,
          offset: nextOffset,
        },
      });

      const got = res.data.items || [];
      all.push(...got);

      const totalCount = Number(res.data.total || 0);
      if (all.length >= totalCount || got.length === 0) break;
      nextOffset += pageLimit;
    }

    return all;
  };

  const runExport = async () => {
    const fieldsOrdered = EXPORT_FIELDS.filter((f) => exportFields.has(f));

    if (fieldsOrdered.length === 0) {
      toast({ title: "Choose at least one field", variant: "destructive" });
      return;
    }

    if (exportScope === "selected" && selected.size === 0) {
      toast({ title: "No leads selected", description: "Select one or more leads first.", variant: "destructive" });
      return;
    }

    setExporting(true);
    try {
      const leads = exportScope === "selected" ? selectedRows : await fetchAllFilteredLeads();
      const rows = [fieldsOrdered];
      leads.forEach((l) => {
        rows.push(fieldsOrdered.map((k) => l[k] ?? ""));
      });

      const stamp = new Date().toISOString().slice(0, 10);
      const filename = exportScope === "selected" ? `leads_selected_${stamp}.csv` : `leads_filtered_${stamp}.csv`;
      downloadCsv(filename, rows);
      toast({ title: "Export downloaded" });
      setExportOpen(false);
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const onDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedRows.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selectedRows.map((l) => api.delete(`/leads/${l.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      // REACT QUERY: Invalidate leads list cache
      invalidateLeadsCache();
      setDeleteConfirmOpen(false);
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const onConvertSelected = async () => {
    try {
      await Promise.all(selectedRows.map((l) => api.post(`/leads/${l.id}/convert`, {})));
      toast({ title: "Converted" });
      // REACT QUERY: Invalidate leads and appointments caches
      invalidateLeadsCache();
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
    } catch (e) {
      toast({ title: "Could not convert", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };




  const clearFilters = () => {
    clearZohoFilters();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="leads-page">

      {/* Controls row on background between title and table */}
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="leads-custom-view"
                aria-label="Custom view"
              >
                {activeViewLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setActiveCustomViewId("all");
                  setFiltersApplied({});
                  setFiltersDraft({});
                  const allColumns = Array.isArray(leadsPrefs.all_table_columns) && leadsPrefs.all_table_columns.length > 0
                    ? leadsPrefs.all_table_columns
                    : DEFAULT_TABLE_COLUMNS;
                  setLeadsPrefs((prev) => ({ ...prev, table_columns: allColumns, active_custom_view_id: "all" }));
                  saveLeadsPrefs({ ...leadsPrefs, active_custom_view_id: "all", table_columns: allColumns });
                  setOffset(0);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                All Leads
              </DropdownMenuItem>
              {(customViews || []).map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => {
                    setActiveCustomViewId(v.id);
                    setFiltersApplied(v.filters_applied || {});
                    setFiltersDraft(v.filters_applied || {});
                    if (Array.isArray(v.columns) && v.columns.length > 0) {
                      setLeadsPrefs((prev) => ({ ...prev, table_columns: v.columns, active_custom_view_id: v.id }));
                    } else {
                      setLeadsPrefs((prev) => ({ ...prev, active_custom_view_id: v.id }));
                    }
                    saveLeadsPrefs({ ...leadsPrefs, active_custom_view_id: v.id, table_columns: Array.isArray(v.columns) ? v.columns : leadsPrefs.table_columns });
                    setOffset(0);
                  }}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                >
                  {v.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => setCustomViewsOpen(true)}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Manage Views…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="leads-bulk-update"
                >
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem onClick={openExportDialog} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onConvertSelected} className="text-slate-100 focus:bg-white/10 focus:text-slate-100">
                  <Plus className="mr-2 h-4 w-4" /> Convert to Appointment
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDeleteSelected} className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <div className="flex w-full flex-col items-end gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            <CollapsibleSearch
              value={q}
              onChange={setQ}
              placeholder="Search leads..."
              width="w-[300px]"
              data-testid="leads-search"
            />

            {/* Filter Button - between search and columns */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setZohoFiltersOpen(!zohoFiltersOpen)}
              aria-label="Open filters"
              data-testid="zoho-filter-icon"
              className="relative"
            >
              <ListFilter className={`h-4 w-4 ${zohoFiltersOpen ? "text-indigo-400" : ""}`} />
              {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" /> : null}
            </Button>

            {/* Household Members Toggle - Icon Button with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Household members visibility"
                  data-testid="leads-household-members-toggle"
                  className={showHouseholdMembers ? "text-indigo-400" : ""}
                >
                  <Users2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem
                  onClick={() => {
                    setShowHouseholdMembers(false);
                    setOffset(0);
                  }}
                  className={`text-slate-100 focus:bg-white/10 focus:text-slate-100 ${!showHouseholdMembers ? "bg-indigo-500/20" : ""}`}
                  data-testid="leads-household-members-hide"
                >
                  Hide Household Members
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setShowHouseholdMembers(true);
                    setOffset(0);
                  }}
                  className={`text-slate-100 focus:bg-white/10 focus:text-slate-100 ${showHouseholdMembers ? "bg-indigo-500/20" : ""}`}
                  data-testid="leads-household-members-show"
                >
                  Show Household Members
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={openColumnsDialog}
            disabled={prefsLoading}
            aria-label="Customize columns"
            data-testid="leads-manage-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="leads-add-trigger"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Leads
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setAddLeadMode("single");
                  setAddLeadOpen(true);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Single Lead
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setAddLeadMode("csv");
                  setAddLeadOpen(true);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
              >
                Upload CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AddLeadDialog
            open={addLeadOpen}
            onOpenChange={setAddLeadOpen}
            mode={addLeadMode}
            onCreated={() => {
              setOffset(0);
              // REACT QUERY: Invalidate leads cache to trigger refetch
              invalidateLeadsCache();
            }}
          />
        </div>
      </div>

      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (zohoFiltersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {zohoFiltersOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="leads-filters-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={zohoFiltersOpen}
                onOpenChange={setZohoFiltersOpen}
                fields={filterFieldDefs}
                optionsByField={optionsByField}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={applyZohoDraft}
                onClear={clearZohoFilters}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card ref={leadsCardRef} className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="leads-all-card">

          <CardContent className="flex h-full flex-col p-0">
            {/* Lead list top toolbar removed (Actions + Manage Columns live in top control bar) */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading…
              </div>
            ) : (
            <div data-testid="leads-table-wrap" className="flex-1 min-w-0 overflow-hidden">
              <div
                className="h-full w-full min-w-0 overflow-auto"
                data-testid="leads-hscroll"
              onWheel={(e) => {
                if (e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.scrollLeft += e.deltaY;
                }
              }}
            >

                <Table style={{ minWidth: tableMinWidthPx }} containerClassName="h-full">
              <TableHeader className="relative">
                <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-3 bg-white/10" />
                <TableRow className="border-transparent hover:bg-transparent">
                  <TableHead colSpan={tableColumns.length + 1} className="h-auto p-1 text-slate-200/70">
                    <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={allOnPageSelected}
                          onCheckedChange={toggleAllOnPage}
                          className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                        />
                      </div>

                      <div className="min-w-0 grid items-center gap-3 pr-2" style={columnsGridStyle}>
                        {tableColumns.map((k) => (
                          <div key={k} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                            <span className="min-w-0 flex-1 truncate">{prettifyFieldLabel(k)}</span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className={
                                    "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                    (sortBy === k ? " bg-white/10" : "")
                                  }
                                  aria-label={`Sort ${prettifyFieldLabel(k)}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                </button>
                              </DropdownMenuTrigger>


                              <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                <DropdownMenuItem
                                  onClick={() => applySort(k, "asc")}
                                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                >
                                  <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => applySort(k, "desc")}
                                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                >
                                  <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={clearSort}
                                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                >
                                  Clear sort
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ))}
                      </div>

                      <div className="h-9 w-9" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow className="border-transparent">
                    <TableCell colSpan={tableColumns.length + 1} className="py-16">
                      <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                        <Users className="h-12 w-12 text-indigo-400 mb-3" />
                        <div className="text-sm text-slate-200/70">No leads found.</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : items.map((lead) => (
                    <TableRow key={lead.id} className="border-transparent">
                      <TableCell colSpan={tableColumns.length + 1} className="p-1 align-top">
                        <button
                          type="button"
                          className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                          onClick={() => navigate(buildPath(`/app/leads/${lead.id}`))}
                        >
                          <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                            <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selected.has(lead.id)}
                                onCheckedChange={() => toggleOne(lead.id)}
                                className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              />
                            </div>
                            <div className="min-w-0 grid grid-cols-1">
                              <div className="grid items-center gap-3 pr-2" style={columnsGridStyle}>
                                {tableColumns.map((k) => (
                                  <div key={k} className="min-w-0">
                                    {k === "status" ? (
                                      <div className="flex items-center gap-1.5">
                                        <Badge
                                          className={"ring-1 " + statusBadgeClass(lead.status)}
                                        >
                                          {statusLabel(lead.status)}
                                        </Badge>
                                        {/* MA-COMPLIANCE-002: TrustedForm status indicators */}
                                        {/* Mode: OFF (gray) - no TrustedForm processing */}
                                        {lead.trustedform?.mode === "off" && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-500/20 ring-1 ring-slate-400/30">
                                                  <Clock className="h-3 w-3 text-slate-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">TrustedForm off — No compliance processing</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Mode: CAPTURE_ONLY (blue) - captured but no enforcement */}
                                        {lead.trustedform?.mode === "capture_only" && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-500/20 ring-1 ring-blue-400/30">
                                                  <CheckCircle className="h-3 w-3 text-blue-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">TrustedForm captured — No enforcement</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Missing Certificate (red) - only in enforce mode */}
                                        {lead.trustedform?.mode !== "off" && lead.trustedform?.mode !== "capture_only" && lead.trustedform?.status === "missing" && !lead.compliance_override && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500/20 ring-1 ring-red-400/30">
                                                  <ShieldX className="h-3 w-3 text-red-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">Missing TrustedForm — Quarantined</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Pending Verification (yellow) - differentiate unverified vs verifying */}
                                        {lead.trustedform?.status === "captured_pending_verification" && !lead.compliance_override && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-yellow-500/20 ring-1 ring-yellow-400/30">
                                                  <Clock className="h-3 w-3 text-yellow-400 animate-pulse" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">
                                                  {lead.trustedform?.verification_mode === "unverified_autoretain_assumed"
                                                    ? "TrustedForm unverified (no API key) — Quarantined"
                                                    : "TrustedForm verification in progress"}
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Compliant (green) - API Verified */}
                                        {lead.trustedform?.status === "retained" && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/30">
                                                  <ShieldCheck className="h-3 w-3 text-emerald-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">TrustedForm compliant (API verified)</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Not Retained (orange) */}
                                        {lead.trustedform?.status === "captured_not_retained" && !lead.compliance_override && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-orange-500/20 ring-1 ring-orange-400/30">
                                                  <AlertTriangle className="h-3 w-3 text-orange-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">TrustedForm not retained — Quarantined</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Legacy: Missing TrustedForm (backward compat) */}
                                        {!lead.trustedform?.status && lead.is_quarantined && lead.quarantine_reason === "missing_trustedform_cert" && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/20 ring-1 ring-amber-400/30">
                                                  <ShieldX className="h-3 w-3 text-amber-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">Missing TrustedForm — Quarantined</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {/* Manual Override (amber check) */}
                                        {(lead.compliance_override || lead.compliance_status === "manual_trustedform_override") && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/20 ring-1 ring-amber-400/30">
                                                  <ShieldCheck className="h-3 w-3 text-amber-400" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                                                <p className="text-xs">Manual compliance override</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>
                                    ) : k === "lead_source" ? (
                                      <Badge className={"ring-1 " + pillClass(lead.lead_source_name || lead.lead_source || "—")}>
                                        {lead.lead_source_name || lead.lead_source || "—"}
                                      </Badge>
                                    ) : k === "attribution_confidence" ? (
                                      <ConfidenceBadge confidence={lead.attribution_confidence} compact />
                                    ) : k === "lead_type" ? (
                                      <Badge className={"ring-1 " + pillClass(lead.lead_type || "—")}>
                                        {lead.lead_type || "—"}
                                      </Badge>
                                    ) : k === "full_name" ? (
                                      <div className="truncate font-medium text-slate-100">{lead.full_name}</div>
                                    ) : (
                                      <div className="truncate text-slate-100/90">{formatCellValue(k, lead[k])}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
              </div>
            </div>
            )}

            <div data-testid="leads-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70">
                {anySelected ? `Selected: ${selected.size}` : `Total Leads: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2">
              {offset > 0 ? (
                <Button
                  variant="secondary"
                  onClick={() => setOffset((o) => Math.max(0, o - limit))}
                >
                  Previous
                </Button>
              ) : null}

              {offset + limit < total ? (
                <Button
                  variant="secondary"
                  onClick={() => setOffset((o) => o + limit)}
                >
                  Next
                </Button>
              ) : null}

              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  const next = Number(v);
                  setLimit(next);
                  setOffset(0);
                  saveLeadsPrefs({ ...leadsPrefs, page_size: next });
                }}
              >
                <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} / Page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>




      <LeadTableColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        fields={LEAD_FIELD_OPTIONS}
        value={tableColumns}
        onSave={(nextColumns) => {
          const nextPrefs = {
            ...leadsPrefs,
            table_columns: nextColumns,
          };
          if (activeCustomViewId === "all") {
            nextPrefs.all_table_columns = nextColumns;
          }
          saveLeadsPrefs(nextPrefs);
        }}
      />

      <CustomViewsDialog
        open={customViewsOpen}
        onOpenChange={setCustomViewsOpen}
        value={customViews}
        currentDraft={{ filters_applied: filtersApplied }}
        currentColumns={tableColumns}
        onSave={(nextViews) => {
          const nextPrefs = {
            ...leadsPrefs,
            custom_views: nextViews,
            active_custom_view_id: activeCustomViewId,
          };
          saveLeadsPrefs(nextPrefs);
        }}
      />


      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl">
          <DialogHeader>
            <DialogTitle>Export CSV</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs text-slate-200/70">Scope</div>
              <Select value={exportScope} onValueChange={setExportScope}>
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  <SelectItem value="selected">Selected Leads</SelectItem>
                  <SelectItem value="filtered">All Leads (Matching Filters)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-200/60">
                {exportScope === "selected" ? `Selected: ${selected.size}` : `Matching current filters: ${total}`}
              </div>
            </div>

            <div className="flex items-end justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setExportFields(new Set(EXPORT_FIELDS))}
              >
                All Fields
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setExportFields(new Set())}
              >
                None
              </Button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-auto rounded-xl bg-black/20 p-3 ring-1 ring-white/10">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {EXPORT_FIELDS.map((f) => (
                <label key={f} className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-white/5">
                  <Checkbox
                    checked={exportFields.has(f)}
                    onCheckedChange={(v) => {
                      setExportFields((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(f);
                        else next.delete(f);
                        return next;
                      });
                    }}
                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                  />
                  <span className="font-mono text-xs text-slate-200/80">{f}</span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setExportOpen(false)}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button
              onClick={runExport}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10" data-testid="leads-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-300">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedRows.length} Lead{selectedRows.length !== 1 ? "s" : ""}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300/80">
              Are you sure you want to delete {selectedRows.length === 1 ? "this lead" : `these ${selectedRows.length} leads`}? 
              This action cannot be undone and will remove all associated data including appointments and clients.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Preview of leads to be deleted */}
          {selectedRows.length > 0 && selectedRows.length <= 5 && (
            <div className="rounded-lg bg-white/5 p-3 space-y-2">
              {selectedRows.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-100 font-medium">{lead.first_name} {lead.last_name || ""}</span>
                  <span className="text-slate-400">{lead.status || "No status"}</span>
                </div>
              ))}
            </div>
          )}
          {selectedRows.length > 5 && (
            <div className="rounded-lg bg-white/5 p-3 text-center text-sm text-slate-400">
              {selectedRows.length} leads selected for deletion
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button
                variant="secondary"
                data-testid="leads-delete-cancel"
              >
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="leads-delete-confirm"
              >
                {deleting ? "Deleting…" : `Delete ${selectedRows.length} Lead${selectedRows.length !== 1 ? "s" : ""}`}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
